/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap-v5'
import clsx from 'clsx'
import { confirmRegistration, resendConfirmationCode } from '../redux/AuthCRUD'
import { useAuth } from 'app/context/auth-context'

interface EmailProvider {
  name: string
  domains: string[]
  inboxUrl: string
}

const EMAIL_PROVIDERS: EmailProvider[] = [
  { name: 'Gmail', domains: ['gmail.com'], inboxUrl: 'https://mail.google.com' },
  { name: 'Outlook', domains: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com'], inboxUrl: 'https://outlook.live.com/mail/' },
  { name: 'Yahoo', domains: ['yahoo.com', 'ymail.com'], inboxUrl: 'https://mail.yahoo.com' },
  { name: 'AOL', domains: ['aol.com'], inboxUrl: 'https://mail.aol.com' },
]

function getSuggestedProvider(email: string): EmailProvider | null {
  const domain = email.split('@')[1]?.toLowerCase()
  if (!domain) return null
  return EMAIL_PROVIDERS.find(p => p.domains.includes(domain)) ?? null
}

function openEmailInbox(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

interface ConfirmationProps {
  email?: string
  password?: string
  asModal?: boolean
  show?: boolean
  autoResend?: boolean
  onConfirmed?: () => void
  onCancel?: () => void
}

export default function Confirmation({
  email: initialEmail = '',
  password,
  asModal = false,
  show = false,
  autoResend = false,
  onConfirmed,
  onCancel,
}: ConfirmationProps): JSX.Element {
  const [email, setEmail] = useState<string>(initialEmail)
  const [confirmationCode, setConfirmationCode] = useState<string>('')
  const [status, setStatus] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [resendTimer, setResendTimer] = useState<number>(0)

  const { login } = useAuth()

  useEffect(() => {
    setEmail(initialEmail || '')
  }, [initialEmail])

  // Resend timer effect
  useEffect(() => {
    if (resendTimer > 0) {
      const timerId = setInterval(() => {
        setResendTimer(prev => prev - 1)
      }, 1000)
      return () => clearInterval(timerId)
    }
  }, [resendTimer])

  const handleResend = useCallback(async () => {
    if (!email) {
      setStatus('Please provide an email address to resend the confirmation code.')
      return
    }

    try {
      await resendConfirmationCode(email.toLowerCase())
      setStatus('Confirmation code resent successfully. Please check your email.')
      setResendTimer(60)
    } catch (error) {
      console.error('Error resending confirmation code:', error)
      setStatus('Failed to resend confirmation code. Please try again later.')
    }
  }, [email])

  // Auto-resend when used as a modal (e.g., opened from Login on UserNotConfirmedException)
  useEffect(() => {
    if (asModal && show && autoResend && email && resendTimer === 0) {
      // best-effort resend when modal opens; swallow errors into status so user sees feedback
      handleResend().catch((err) => {
        console.warn('Auto resend failed', err)
      })
    }
    // intentionally include handleResend to ensure latest email is used
  }, [asModal, show, autoResend, email, resendTimer, handleResend])

  const handleConfirm = useCallback(async () => {
    if (!email) {
      setStatus('Please provide an email address.')
      return
    }
    if (!confirmationCode || confirmationCode.length < 6) {
      setStatus('Please enter the 6 digit confirmation code.')
      return
    }

    setLoading(true)
    setStatus('')

    try {
      await confirmRegistration(email.toLowerCase(), confirmationCode)
      // Attempt sign in if password is provided
      if (password) {
        try {
          await login(email.toLowerCase(), password)
        } catch (signInErr) {
          // If sign in fails, still treat confirmation as successful.
          console.warn('Sign in after confirmation failed', signInErr)
        }
      }
      setStatus('')
      onConfirmed?.()
    } catch (error: any) {
      console.error('Confirmation error', error)
      const code = error?.code || error?.name
      const msgMap: Record<string, string> = {
        'CodeMismatchException': 'The confirmation code you entered is incorrect. Please try again.',
        'ExpiredCodeException': 'The confirmation code has expired. Please request a new code.',
        'UserNotFoundException': 'No account found for this email. Please register again.',
        'NotAuthorizedException': 'This account is already confirmed. Please sign in.',
        'LimitExceededException': 'Attempt limit exceeded, please try again later.',
        'UnexpectedLambdaException': 'A server error occurred during confirmation. Please try again in a moment.',
        'UserLambdaValidationException': 'Server validation failed. Please try again or contact support.',
      }

      let errorMessage = msgMap[code] || error?.message || 'Confirmation failed. Please try again.'

      // Handle specific Lambda timeout/failure messages that might not have a clean code
      if (errorMessage.includes('Lambda') || errorMessage.includes('PostConfirmation')) {
        errorMessage = 'Finalizing your account is taking longer than expected. Please wait a moment and click "Confirm" again to retry.'
      }

      setStatus(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [email, confirmationCode, password, login, onConfirmed])

  const suggestedProvider = useMemo(() => getSuggestedProvider(email), [email])

  const otherProviders = useMemo(
    () => EMAIL_PROVIDERS.filter(p => p.name !== suggestedProvider?.name),
    [suggestedProvider]
  )

  const content = (
    <>
      <div className='text-center mb-10'>
        <h3 className='text-primary mb-3'>Confirmation Code</h3>

        {email && (
          <div className='border border-gray-200 rounded-4 bg-light p-4 mb-4 text-start'>
            <div className='d-flex align-items-start justify-content-between gap-3 flex-wrap'>
              <div>
                <p className='text-muted text-uppercase fs-8 fw-semibold mb-1'>Verification code sent to</p>
                <p className='fw-bold fs-5 text-gray-800 mb-0 text-break'>{email}</p>
              </div>
              {suggestedProvider && (
                <span className='badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2'>
                  {suggestedProvider.name} detected
                </span>
              )}
            </div>
          </div>
        )}

        {suggestedProvider && (
          <div className='border border-primary border-opacity-10 rounded-4 bg-white p-4 shadow-sm mb-3 text-start'>
            <div className='d-flex align-items-center justify-content-between gap-3 mb-3'>
              <div>
                <p className='text-muted text-uppercase fs-8 fw-semibold mb-1'>Quick inbox access</p>
                <h5 className='mb-0 text-gray-800'>Open your {suggestedProvider.name} inbox</h5>
              </div>
              <i className='bi bi-envelope-paper fs-1 text-primary opacity-75'></i>
            </div>
            <button
              type='button'
              className='btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-2'
              onClick={() => openEmailInbox(suggestedProvider.inboxUrl)}
            >
              <i className='bi bi-box-arrow-up-right fs-4'></i>
              Open {suggestedProvider.name}
            </button>
            <div className='text-muted fs-7 text-center mt-2'>
              Opens your inbox in a new tab so you can grab the confirmation code quickly.
            </div>
            {otherProviders.length > 0 && (
              <div className='mt-4'>
                <div className='separator separator-dashed mb-4'></div>
                <p className='text-muted text-uppercase fs-8 fw-semibold mb-3'>Other inbox options</p>
                <div className='d-flex flex-wrap gap-2'>
                  {otherProviders.map(provider => (
                    <button
                      key={provider.name}
                      type='button'
                      className='btn btn-outline-secondary btn-sm rounded-pill px-4'
                      onClick={() => openEmailInbox(provider.inboxUrl)}
                    >
                      {provider.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <p className='mb-2'>If you didn't receive the code, check spam or resend it below.</p>
        <button
          type='button'
          className='btn btn-link text-primary fw-semibold text-decoration-none px-0'
          onClick={handleResend}
          disabled={resendTimer > 0}
        >
          {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Confirmation Code'}
        </button>
        <p className='text-muted'>Enter the confirmation code sent to your email:</p>
      </div>

      {status && (
        <div className='mb-4 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{status}</div>
        </div>
      )}

      <div className='fv-row mb-7'>
        <input
          type='text'
          autoFocus
          autoComplete='off'
          value={confirmationCode}
          onChange={(e) => setConfirmationCode(e.target.value)}
          className={clsx('form-control form-control-lg form-control-solid', {
            'is-invalid': confirmationCode.length > 0 && confirmationCode.length !== 6,
          })}
          placeholder='Enter 6 digit code'
        />
      </div>

      <div className='d-flex justify-content-center'>
        <button
          type='button'
          className='btn btn-lg btn-primary mb-5'
          onClick={handleConfirm}
          disabled={loading}
        >
          {!loading ? (
            <span className='indicator-label'>
              <i className='bi bi-arrow-right-circle-fill fs-2 me-2'></i>
              Confirm
            </span>
          ) : (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...{' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </>
  )

  if (asModal) {
    return (
      <Modal show={!!show} backdrop='static' keyboard={false} centered size='xl'>
        <Modal.Header className='border-0 pb-0 align-items-start'>
          <Modal.Title>Account Confirmation</Modal.Title>
          <Button variant='light' onClick={() => onCancel?.()} disabled={loading} className='ms-auto'>
            Cancel
          </Button>
        </Modal.Header>
        <Modal.Body className='pt-4 pb-3'>
          <div className='row g-5 g-lg-8 align-items-stretch'>
            <div className='col-12 col-lg-6 pe-lg-8'>
              <div className='p-4 h-100 d-flex flex-column'>
                <div className='mb-3'>
                  <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
                  <input
                    type='email'
                    className='form-control form-control-lg form-control-solid'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={!!initialEmail}
                  />
                </div>

                <div className='mb-3'>
                  <h3 className='text-primary mb-2'>Confirmation Code</h3>
                  <p className='text-muted mb-0'>Enter the 6-digit code from your email.</p>
                </div>

                {status && (
                  <div className='mb-3 alert alert-danger py-3'>
                    <div className='alert-text font-weight-bold'>{status}</div>
                  </div>
                )}

                <div className='fv-row mb-3'>
                  <input
                    type='text'
                    autoFocus
                    autoComplete='off'
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    className={clsx('form-control form-control-lg form-control-solid', {
                      'is-invalid': confirmationCode.length > 0 && confirmationCode.length !== 6,
                    })}
                    placeholder='Enter 6 digit code'
                  />
                </div>

                <div className='d-flex justify-content-center'>
                  <button
                    type='button'
                    className='btn btn-lg btn-primary px-8 d-inline-flex align-items-center gap-2'
                    onClick={handleConfirm}
                    disabled={loading}
                  >
                    {!loading ? (
                      <span className='indicator-label'>
                        <i className='bi bi-arrow-right-circle-fill fs-2 me-2'></i>
                        Confirm
                      </span>
                    ) : (
                      <span className='indicator-progress' style={{ display: 'block' }}>
                        Please wait...{' '}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>

                <div className='border-top pt-3 mt-4'>
                  <p className='mb-2 fw-semibold'>Need another code?</p>
                  <p className='text-muted mb-2'>Check spam first, then resend if needed.</p>
                  <button
                    type='button'
                    className='btn btn-link text-primary fw-semibold text-decoration-none px-0'
                    onClick={handleResend}
                    disabled={resendTimer > 0}
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Confirmation Code'}
                  </button>
                </div>
              </div>
            </div>

            <div className='col-12 d-lg-none'>
              <div className='separator separator-dashed my-1'></div>
            </div>

            <div
              className='col-12 col-lg-6 ps-lg-8'
              style={{ borderLeft: '2px dotted #a1a5b7' }}
            >
              <div className='bg-white border border-gray-200 rounded-4 p-4 h-100 shadow-sm'>
                <div className='d-flex align-items-start justify-content-between gap-3 mb-3'>
                  <div>
                    <p className='text-muted text-uppercase fs-8 fw-semibold mb-1'>Instructions</p>
                    <h4 className='mb-1 text-gray-800'>Open your inbox</h4>
                    <p className='text-muted mb-0'>Use your email provider tab to copy the confirmation code.</p>
                  </div>
                  <div className='symbol symbol-60px symbol-circle bg-primary bg-opacity-10 flex-shrink-0'>
                    <div className='symbol-label bg-transparent'>
                      <i className='bi bi-envelope-open fs-1 text-primary'></i>
                    </div>
                  </div>
                </div>

                <div className='border border-info border-opacity-10 rounded-4 bg-info bg-opacity-5 p-3 mb-3 d-flex align-items-start gap-3'>
                  <i className='bi bi-info-circle-fill fs-2 text-info mt-1'></i>
                  <p className='mb-0 text-gray-700 fs-7 lh-base'>
                    If you don&apos;t see the confirmation email, check your Spam folder. To preclude in the future, add carl@videojobfairs.com to your Safe Senders list.
                  </p>
                </div>

                {email && (
                  <div className='border border-gray-200 rounded-4 bg-light p-4 mb-3'>
                    <p className='text-muted text-uppercase fs-8 fw-semibold mb-1'>Verification code sent to</p>
                    <p className='fw-bold fs-5 text-gray-800 mb-0 text-break'>{email}</p>
                    {suggestedProvider && (
                      <div className='mt-3'>
                        <span className='badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2'>
                          {suggestedProvider.name} detected
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {suggestedProvider && (
                  <div className='border border-dark rounded-4 bg-primary bg-opacity-5 p-4 mb-3'>
                    <div className='d-flex align-items-center justify-content-between gap-3 mb-3'>
                      <div>
                        <p className='text-muted text-uppercase fs-8 fw-semibold mb-1'>Recommended inbox</p>
                        <h5 className='mb-0 text-gray-800'>Open your {suggestedProvider.name} inbox</h5>
                      </div>
                      <i className='bi bi-box-arrow-up-right fs-1 text-primary opacity-75'></i>
                    </div>
                    <button
                      type='button'
                      className='btn btn-link text-primary fw-semibold text-decoration-none p-0 d-flex align-items-center justify-content-center gap-2 mx-auto'
                      onClick={() => openEmailInbox(suggestedProvider.inboxUrl)}
                    >
                      <i className='bi bi-envelope-open fs-4 text-secondary'></i>
                      Open {suggestedProvider.name}
                    </button>
                    <div className='text-muted fs-7 text-center mt-3'>
                      Opens in a new tab so you can copy the code quickly.
                    </div>
                  </div>
                )}

                {otherProviders.length > 0 && (
                  <div>
                    <p className='text-muted text-uppercase fs-8 fw-semibold mb-3'>Other providers</p>
                    <div className='d-flex flex-wrap gap-2'>
                      {otherProviders.map(provider => (
                        <button
                          key={provider.name}
                          type='button'
                          className='btn btn-outline-secondary btn-sm rounded-pill px-4'
                          onClick={() => openEmailInbox(provider.inboxUrl)}
                        >
                          {provider.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <div>
      <div className='fv-row mb-4'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          type='email'
          className='form-control form-control-lg form-control-solid'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={!!initialEmail}
        />
      </div>
      {content}
    </div>
  )
}
